"use client";

import { ExternalLink, Link2, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useBackAwarePush } from "@/hooks/use-back-aware-push";
import type { FeedLinkSummary } from "@/lib/feed/rich-content";
import { internalAppHref } from "@/lib/link-url";
import { cn } from "@/lib/utils";

type PostLinkPreviewProps = {
  link: FeedLinkSummary;
  title?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  compact?: boolean;
  onDismiss?: () => void;
};

export function PostLinkPreview({
  link,
  title,
  description,
  imageUrl,
  compact = false,
  onDismiss,
}: PostLinkPreviewProps) {
  const resolvedTitle = title?.trim() || link.host;
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const visibleImage = imageUrl && failedImage !== imageUrl ? imageUrl : null;
  const internalHref = internalAppHref(link.url);
  const push = useBackAwarePush();

  return (
    <div
      className={cn(
        "group relative block overflow-hidden rounded-xl border border-border bg-surface-alt transition hover:border-brand/40",
        compact ? "mt-2" : "mt-3",
      )}
    >
      {onDismiss ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full bg-black/60 text-white hover:bg-black/75"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDismiss();
          }}
          aria-label="Dismiss link preview"
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}

      <a
        href={link.url}
        target={internalHref ? undefined : "_blank"}
        rel="noopener noreferrer"
        onClick={(event) => {
          event.stopPropagation();
          if (internalHref) {
            event.preventDefault();
            push(internalHref);
          }
        }}
        className="block"
      >
        {visibleImage ? (
          // biome-ignore lint/performance/noImgElement: external OG images should bypass app image assumptions.
          <img
            src={visibleImage}
            alt={resolvedTitle}
            className="aspect-[16/9] w-full object-cover"
            loading="lazy"
            onError={() => setFailedImage(visibleImage)}
          />
        ) : null}

        <div className="flex gap-3 p-3">
          {!visibleImage ? (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background text-brand">
              <Link2 className="h-5 w-5" />
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
              <span className="truncate">{link.host}</span>
              <ExternalLink className="h-3 w-3 shrink-0" />
            </div>
            <p className="mt-1 line-clamp-2 text-sm font-semibold text-text-primary">
              {resolvedTitle}
            </p>
            {description ? (
              <p className="mt-1 line-clamp-2 text-xs text-text-secondary">
                {description}
              </p>
            ) : null}
          </div>
        </div>
      </a>
    </div>
  );
}
